import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { LanguageUtils } from '../../utils/language.utils';
import { Attachment, IAttachmentsService } from './attachments.interface';
import { AuthService } from '../../auth/auth.service';
import { Exception } from '../exceptions/exceptions.interface';

@Injectable({
  providedIn: 'root'
})
export class AttachmentsService implements IAttachmentsService {
  cacheVehicle = new Map<string, Attachment>();
  cacheApplicant = new Map<string, Attachment>();

  constructor(
    private httpClient: HttpClient,
    private readonly authService: AuthService
  ) { }
  
  createExceptionAttachment(applicant: string, plate: string, exception: string, attachment: Attachment): Observable<Exception> {
    return this.httpClient.post<Exception>(environment.apiPublicUrl + '/registration/applicant/exception/attachment?applicant=' + applicant + '&vehicle=' + plate + '&exception=' + exception + '&language=' + LanguageUtils.getLanguage(),
    {
      attachments: [attachment]
    });
  }

  getExceptionAttachment(applicant: string, plate: string, exception: string, fileId: string): Observable<Attachment> {
    const cached = this.cacheVehicle.get(LanguageUtils.getLanguage() + '-' + fileId);
    if (cached) {
      return of(cached);
    } else {
      //    https://zbe.cat:8002/zbe/exceptions/registration/applicant/exception/attachment?applicant=00000000T&vehicle=0000AAA&exception=24070aba-3494-4375-be5a-ee753d6de656&attachment=c7081049-d1dc-45a7-a8b1-876992c70e6a&language=es-ES
      return this.httpClient.get<Attachment>(environment.apiPublicUrl + '/registration/applicant/exception/attachment?applicant=' + applicant + '&vehicle=' + plate + '&exception=' + exception + '&attachment=' + fileId + '&language=' + LanguageUtils.getLanguage()).pipe(
        tap(data => this.cacheVehicle.set(LanguageUtils.getLanguage() + '-' + fileId, data))
      );
    }
  }
  getApplicantAttachment(fileId: string): Observable<Attachment> {
    const cached = this.cacheApplicant.get(LanguageUtils.getLanguage() + '-' + fileId);
    if (cached) {
      return of(cached);
    } else {
      // https://{{server}}:8002/zbe/exceptions/registration/applicant/attachment/file?applicant=00000000T&attachment=2fd9de1c-0172-450e-9679-88726aa57946&language=es-ES
      return this.httpClient.get<Attachment>(environment.apiPublicUrl + '/registration/applicant/attachment?applicant=' + this.authService.getCurrentUser().documentNumber + '&attachment=' + fileId + '&language=' + LanguageUtils.getLanguage()).pipe(
        tap(data => this.cacheApplicant.set(LanguageUtils.getLanguage() + '-' + fileId, data))
      );
    }
  }
  deleteExceptionAttachment(applicant: string, plate: string, exception: string, fileId: string): Observable<string> {
    // https://{{server}}:8002/zbe/exceptions/registration/applicant/exception/attachment?applicant=00000000T&vehicle=0000AAA&exception=924d73ba-7b69-453a-9ae4-80d67e489231&attachment=685ec995-402c-4b0e-a99e-5f9dc8395ed7&language=es-ES
    return this.httpClient.delete<string>(environment.apiPublicUrl + '/registration/applicant/exception/attachment?applicant=' + applicant + '&vehicle=' + plate + '&exception=' + exception + '&attachment=' + fileId + '&language=' + LanguageUtils.getLanguage());
  }
  deleteApplicantAttachment(fileId: string): Observable<string> {
    // https://{{server}}:8002/zbe/exceptions/registration/applicant/attachment?applicant=00000000T&attachment=56a619f0-d269-4db2-b26f-3b68f8ad50da&language=es-ES
    return this.httpClient.delete<string>(environment.apiPublicUrl + '/registration/applicant/attachment?applicant=' + this.authService.getCurrentUser().documentNumber + '&attachment=' + fileId + '&language=' + LanguageUtils.getLanguage());
  }

  // PRIVATE

  getExceptionAttachmentPrivate(applicant: string, plate: string, exception: string, fileId: string): Observable<Attachment> {
    const cached = this.cacheVehicle.get(LanguageUtils.getLanguage() + '-private-' + fileId);
    if (cached) {
      return of(cached);
    } else {
      return this.httpClient.get<Attachment>(environment.apiPrivateUrl + '/registration/applicant/exception/attachment/private?applicant=' + applicant + '&vehicle=' + plate + '&exception=' + exception + '&attachment=' + fileId + '&language=' + LanguageUtils.getLanguage()).pipe(
        tap(data => this.cacheVehicle.set(LanguageUtils.getLanguage() + '-private-' + fileId, data))
      );
    }
  }
  createExceptionAttachmentPrivate(applicant: string, plate: string, exception: string, attachment: Attachment): Observable<Attachment> {
    // Post: https://{{server}}:8002/zbe/exceptions/registration/applicant/exception/attachment/private?applicant=00000000T&vehicle=0000AAA&exception=924d73ba-7b69-453a-9ae4-80d67e489231&language=es-ES
    // Attachments: [attachment]
    return this.httpClient.post<Attachment>(environment.apiPrivateUrl + '/registration/applicant/exception/attachment/private?applicant=' + applicant + '&vehicle=' + plate + '&exception=' + exception + '&language=' + LanguageUtils.getLanguage(), {
      attachments: [attachment]
    });
  }
  deleteExceptionAttachmentPrivate(applicant: string, plate: string, exception: string, fileId: string): Observable<string> {
    return this.httpClient.delete<string>(environment.apiPrivateUrl + '/registration/applicant/exception/attachment/private?applicant=' + applicant + '&vehicle=' + plate + '&exception=' + exception + '&attachment=' + fileId + '&language=' + LanguageUtils.getLanguage());
  }

  validateExceptionAttachmentPrivate(applicant: string, plate: string, exception: string, fileId: string): Observable<Attachment> {
    return this.httpClient.post<Attachment>(environment.apiPrivateUrl + '/registration/applicant/exception/attachment/validation?applicant=' + applicant + '&vehicle=' + plate + '&exception=' + exception + '&attachment=' + fileId + '&validation=true' + '&language=' + LanguageUtils.getLanguage(), null);
  }
  invalidateExceptionAttachmentPrivate(applicant: string, plate: string, exception: string, fileId: string): Observable<Attachment> {
    return this.httpClient.post<Attachment>(environment.apiPrivateUrl + '/registration/applicant/exception/attachment/validation?applicant=' + applicant + '&vehicle=' + plate + '&exception=' + exception + '&attachment=' + fileId + '&validation=false' + '&language=' + LanguageUtils.getLanguage(), null);
  }
}
